import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../Global/Layout';
import DataTable from '../../Global/DataTable';
import { showError } from '../../../utils/sweetAlertConfig';
import { LottoBallsDisplay } from '../../../utils/LottoBalls';
import { API_CONFIG, ICON_ACTION, LOTTO_TYPE, dateStampToDateDB, parseDatetimeLocal } from '../../../contexts/constants';
import DatePicker, { registerLocale } from 'react-datepicker';
import { th } from 'date-fns/locale';
import 'react-datepicker/dist/react-datepicker.css';

registerLocale('th', th);

const ManagePrizesHistory = ({ onLogout, userRole }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [historyData, setHistoryData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);

  const [showSearchForm, setShowSearchForm] = useState(false);
  const [searchFilters, setSearchFilters] = useState({
    types: {
      powerball: false,
      megamillions: false
    },
    statuses: {
      active: false,
      inactive: false
    },
    summaryStatuses: {
      summarized: false,
      notSummarized: false
    }
  });
  const [appliedSearchFilters, setAppliedSearchFilters] = useState({
    types: {
      powerball: false,
      megamillions: false
    },
    statuses: {
      active: false,
      inactive: false
    },
    summaryStatuses: {
      summarized: false,
      notSummarized: false
    }
  });
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [appliedStartDate, setAppliedStartDate] = useState(null);
  const [appliedEndDate, setAppliedEndDate] = useState(null);

  useEffect(() => {
    fetchHistory();
  }, [appliedSearchFilters, appliedStartDate, appliedEndDate, currentPage, pageSize]);

 	if (!['sysadmin', 'owner', 'admin'].includes(userRole)) {
		window.location.href = '/forbidden';
		return null;
	}

  const fetchHistory = async () => {
    try {
      setLoading(true);

      // Build query parameters
      const params = new URLSearchParams();

      // Handle types
      const selectedTypes = Object.keys(appliedSearchFilters.types).filter(type => appliedSearchFilters.types[type]);
      if (selectedTypes.length > 0) {
        // If only one type is selected, send that type
        if (selectedTypes.length === 1) {
          params.append('type', selectedTypes[0]);
        }
        // If both are selected, don't send type parameter (means all)
      }

      // Handle statuses
      const selectedStatuses = Object.keys(appliedSearchFilters.statuses).filter(status => appliedSearchFilters.statuses[status]);
      if (selectedStatuses.length > 0) {
        // If only one status is selected, send that status
        if (selectedStatuses.length === 1) {
          params.append('status', selectedStatuses[0] === 'active' ? 'true' : 'false');
        }
        // If both are selected, don't send status parameter (means all)
      }

      // Handle summary statuses
      const selectedSummaryStatuses = Object.keys(appliedSearchFilters.summaryStatuses).filter(status => appliedSearchFilters.summaryStatuses[status]);
      if (selectedSummaryStatuses.length > 0) {
        // If only one status is selected, send that status
        if (selectedSummaryStatuses.length === 1) {
          params.append('summaryStatus', selectedSummaryStatuses[0] === 'summarized' ? 'true' : 'false');
        }
        // If both are selected, don't send summaryStatus parameter (means all)
      }

      if (appliedStartDate) {
        params.append('startDate', dateStampToDateDB(appliedStartDate));
      }
      if (appliedEndDate) {
        params.append('endDate', dateStampToDateDB(appliedEndDate));
      }

      // Add pagination parameters
      params.append('page', currentPage);
      params.append('limit', pageSize);

      const token = localStorage.getItem('token');
      const response = await fetch(`${API_CONFIG.BASE_URL}/awards?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch award results');
      }

      const data = await response.json();

      // Handle pagination response - check if API returns { data, total } or just array
      if (data.data && Array.isArray(data.data)) {
        setHistoryData(data.data);
        setTotalItems(data.total || data.data.length);
      } else if (Array.isArray(data)) {
        setHistoryData(data);
        setTotalItems(data.length);
      } else {
        setHistoryData([]);
        setTotalItems(0);
      }
    } catch (error) {
      console.error('Error fetching history:', error);
      showError('เกิดข้อผิดพลาด', 'ไม่สามารถโหลดข้อมูลประวัติได้');
      setHistoryData([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      key: 'actions',
      header: 'จัดการ',
      className: 'text-center',
      width: 50,
      render: (row) => (
        <div className="flex gap-2 justify-center">
          <button
            onClick={() => handleView(row)}
            className="text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 rounded transition"
            title="ดูรายละเอียด"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
          </button>
          {(row.summaryStatus === false/* || ['sysadmin', 'owner'].includes(userRole)*/) ?<button
            onClick={() => handleEdit(row)}
            className="text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded transition"
            title="แก้ไข"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </button> : null}
          {/* <button
            onClick={() => handleDelete(row)}
            className="text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded transition"
            title="ลบ"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button> */}
        </div>
      )
    },
    {
      key: 'drawDate',
      header: 'วันที่ออกรางวัล',
      render: (row) => (
        <div className="text-center">
          <div className="font-semibold">{parseDatetimeLocal(row.drawDate).toLocaleDateString('th-TH')}</div>
          <div className="text-xs text-gray-500 dark:text-gray-400">{row.drawTime} น.</div>
        </div>
      )
    },
    {
      key: 'type',
      header: 'ประเภท',
      render: (row) => (
        <div className="text-center">
          <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold min-w-36 ${
            row.type === 'powerball'
              ? 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300'
              : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300'
          }`}>
            {LOTTO_TYPE[row.type]}
          </span>
        </div>
      )
    },
    {
      key: 'numbers',
      header: 'เลขที่ออก',
      render: (row) => <LottoBallsDisplay grayBalls={(row.winningNumbers || []).length > 0 ? row.winningNumbers : ['', '', '', '', '']} specialBall={row.powerBall} type={row.type} />
    },
    {
      key: 'jackpot',
      header: 'แจ็กพอต (บาท)',
      render: (row) => (
        <div className="text-center">
          <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
            row.status
              ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
              : 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300'
          }`}>
            {row.status ? 'เปิดรับคำสั่งซื้อ' : 'ปิดรับคำสั่งซื้อ'}
          </span>
        </div>
      )
    },
    {
      key: 'summaryStatus',
      header: 'สถานะ',
      render: (row) => (
        <div className="text-center">
          <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
            row.summaryStatus
              ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
              : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
          }`}>
            {row.summaryStatus ? 'สรุปแล้ว' : 'ยังไม่สรุป'}
          </span>
        </div>
      )
    },
    // {
    //   key: 'jackpot',
    //   header: 'แจ็กพอต (เครดิต)',
    //   render: (row) => (
    //     <div className="text-center font-semibold text-red-600 dark:text-red-400">
    //       {row.jackpot}
    //     </div>
    //   )
    // }
    {
      key: 'action',
      header: 'คำสั่งซื้อ',
      render: (row) => (
        <div className="text-center">
          <button
            onClick={
              () => navigate('/members/history-bet?pr=' + row.id + '&nre=1')
            }
            className="py-1 px-3 text-sm text-white rounded-lg transition shadow-lg bg-blue-500 hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-700"
            title="รายการสั่งซื้อ"
          >
            รายการสั่งซื้อ
          </button>
        </div>
      )
    },
    {
      key: 'membersWinning',
      header: 'ผู้ใช้งาน',
      render: (row) => (
        <div className="text-center">
          <button
            onClick={() => {
              const params = new URLSearchParams({
                periodId: row.id,
                lotType: row.type,
                periodLabel: parseDatetimeLocal(row.drawDate).toLocaleDateString('th-TH', { year: 'numeric', month: 'long', day: 'numeric' }),
              });
              navigate(`/report/members-winning?${params.toString()}`);
            }}
            className="py-1 px-3 text-sm text-white rounded-lg transition shadow-lg bg-blue-500 hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-700"
            title="รายการผู้ใช้งาน"
          >
            รายการผู้ใช้งาน
          </button>
        </div>
      )
    }
  ];

  const handleView = (row) => {
    console.log('View:', row);
    // Navigate to edit page with view mode
    row.type === 'powerball' ? navigate('/manage_data/prizes-manage-powerball', { state: { data: row, mode: 'view' } }) : navigate('/manage_data/prizes-manage-megamillions', { state: { data: row, mode: 'view' } });
  };

  const handleEdit = (row) => {
    // console.log('Edit:', row);
    // Navigate to edit page
    row.type === 'powerball' ? navigate('/manage_data/prizes-manage-powerball', { state: { data: row, mode: 'edit' } }) : navigate('/manage_data/prizes-manage-megamillions', { state: { data: row, mode: 'edit' } });
  };

  const toggleSearchForm = () => {
		setShowSearchForm(!showSearchForm);
	};

  const handleSearch = () => {
    setAppliedSearchFilters({ ...searchFilters });
    setAppliedStartDate(startDate);
    setAppliedEndDate(endDate);
    setCurrentPage(1);
  };

  const handleClearSearch = () => {
    const initialFilters = {
      types: {
        powerball: false,
        megamillions: false
      },
      statuses: {
        active: false,
        inactive: false
      },
      summaryStatuses: {
        summarized: false,
        notSummarized: false
      }
    };
    setSearchFilters(initialFilters);
    setAppliedSearchFilters(initialFilters);
    setStartDate(null);
    setEndDate(null);
    setAppliedStartDate(null);
    setAppliedEndDate(null);
    setCurrentPage(1);
  };

  const SearchForm = () => (
    <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow mb-4">
      <div className="space-y-4">
        {/* แถวที่ 1: ช่วงวันที่ */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              วันที่เริ่มต้น
            </label>
            <DatePicker
              selected={startDate}
              onChange={(date) => setStartDate(date)}
              selectsStart
              startDate={startDate}
              endDate={endDate}
              dateFormat="dd/MM/yyyy"
              locale="th"
              placeholderText="เลือกวันที่เริ่มต้น"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              wrapperClassName="w-full"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              วันที่สิ้นสุด
            </label>
            <DatePicker
              selected={endDate}
              onChange={(date) => setEndDate(date)}
              selectsEnd
              startDate={startDate}
              endDate={endDate}
              minDate={startDate}
              dateFormat="dd/MM/yyyy"
              locale="th"
              placeholderText="เลือกวันที่สิ้นสุด"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              wrapperClassName="w-full"
            />
          </div>
        </div>

        {/* แถวที่ 2: ประเภทและสถานะรับคำสั่งซื้อ */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* ประเภท */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              ประเภท
            </label>
            <div className="space-x-2 flex">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={searchFilters.types.powerball}
                  onChange={(e) => setSearchFilters({
                    ...searchFilters,
                    types: { ...searchFilters.types, powerball: e.target.checked }
                  })}
                  className="w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">{LOTTO_TYPE.powerball}</span>
              </label>
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={searchFilters.types.megamillions}
                  onChange={(e) => setSearchFilters({
                    ...searchFilters,
                    types: { ...searchFilters.types, megamillions: e.target.checked }
                  })}
                  className="w-4 h-4 text-yellow-600 border-gray-300 rounded focus:ring-yellow-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">{LOTTO_TYPE.megamillions}</span>
              </label>
            </div>
          </div>

          {/* สถานะรับคำสั่งซื้อ */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              สถานะรับคำสั่งซื้อ
            </label>
            <div className="space-x-2 flex">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={searchFilters.statuses.active}
                  onChange={(e) => setSearchFilters({
                    ...searchFilters,
                    statuses: { ...searchFilters.statuses, active: e.target.checked }
                  })}
                  className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">เปิด</span>
              </label>
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={searchFilters.statuses.inactive}
                  onChange={(e) => setSearchFilters({
                    ...searchFilters,
                    statuses: { ...searchFilters.statuses, inactive: e.target.checked }
                  })}
                  className="w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">ปิด</span>
              </label>
            </div>
          </div>
        </div>

        {/* แถวที่ 3: สถานะ */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              สถานะ
            </label>
            <div className="space-x-2 flex">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={searchFilters.summaryStatuses.summarized}
                  onChange={(e) => setSearchFilters({
                    ...searchFilters,
                    summaryStatuses: { ...searchFilters.summaryStatuses, summarized: e.target.checked }
                  })}
                  className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">สรุปแล้ว</span>
              </label>
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={searchFilters.summaryStatuses.notSummarized}
                  onChange={(e) => setSearchFilters({
                    ...searchFilters,
                    summaryStatuses: { ...searchFilters.summaryStatuses, notSummarized: e.target.checked }
                  })}
                  className="w-4 h-4 text-gray-600 border-gray-300 rounded focus:ring-gray-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">ยังไม่สรุป</span>
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* ปุ่มค้นหาและล้าง */}
      <div className="flex justify-end gap-2 mt-4">
        <button
          onClick={handleSearch}
          className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-md font-medium transition-colors flex items-center gap-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          ค้นหา
        </button>
        <button
          onClick={handleClearSearch}
          className="px-4 py-2 bg-yellow-500 hover:bg-yellow-600 text-white rounded-md font-medium transition-colors flex items-center gap-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          ล้าง
        </button>
      </div>
    </div>
  );

  const gotoPage = (p) => {
    const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
    if (p < 1 || p > totalPages) return;
    setCurrentPage(p);
  };


  return (
    <Layout title="รายการตารางทุกงวด" onLogout={onLogout} userRole={userRole}>
      <div className="mb-4 flex flex-wrap justify-end sm:justify-between items-center gap-2 text-right">
        <p className="w-full sm:w-auto text-gray-600 dark:text-gray-400 text-left">
          รายการตารางทุกงวดทั้งหมด
        </p>
        {/* Toggle Search */}
        <div className='flex gap-3'>
          <button
            onClick={toggleSearchForm}
            className={`flex items-center gap-2 px-4 py-2 text-white rounded-lg transition shadow-lg ${
              showSearchForm
              ? 'bg-red-500 hover:bg-red-600 dark:bg-red-600 dark:hover:bg-red-700'
              : 'bg-blue-500 hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-700'
            }`}
          >
            {showSearchForm ? ICON_ACTION.CLOSE : ICON_ACTION.SEARCH}
            <span className='hidden md:inline'>ค้นหา</span>
          </button>
          {/* <button
            onClick={() => navigate('/manage_data/prizes-manage-powerball')}
            className="px-6 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors font-medium shadow-md flex items-center gap-2"
          >
            {ICON_ACTION.ADD}
            <span className='hidden md:inline'>เพิ่มตาราง {LOTTO_TYPE.powerball} งวดใหม่</span>
          </button>
          <button
            onClick={() => navigate('/manage_data/prizes-manage-megamillions')}
            className="px-6 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg transition-colors font-medium shadow-md flex items-center gap-2"
          >
            {ICON_ACTION.ADD}
            <span className='hidden md:inline'>เพิ่มตาราง {LOTTO_TYPE.megamillions} งวดใหม่</span>
          </button> */}
        </div>
      </div>

      {showSearchForm && (
        <SearchForm />
      )}

        <DataTable
          columns={columns}
          data={historyData}
          loading={loading}
          page={currentPage}
          pageSize={pageSize}
          totalItems={totalItems}
          onPageChange={gotoPage}
          onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
          emptyMessage="ไม่มีข้อมูลประวัติ"
          showPagination={true}
        />
    </Layout>
  );
};

export default ManagePrizesHistory;
